import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, map, Observable, of, tap } from 'rxjs';

import { switchMapToVoid } from '@ideals/utils/void';

import { DiagnosticStatus, ErrorDetails, ErrorReport } from '../../models';
import { TROUBLESHOOTING_CONFIG } from '../../troubleshooting.token';

import { Troubleshooting } from './troubleshooting';

interface ErrorReportDetails {
  readonly domain: string;
}

export class CheckHealthUrlsService extends Troubleshooting {
  readonly #config = inject(TROUBLESHOOTING_CONFIG);
  readonly #httpClient = new HttpClient(inject(HttpBackend));
  readonly #translateService = inject(TranslateService);

  #errorDomains: string[] = [];
  #errorReports: ErrorReport<ErrorReportDetails>[] = [];
  #status: DiagnosticStatus = 'Default';

  override getErrorDetails(): ErrorDetails {
    return {
      title: this.#translateService.instant('help_modal.TEXT.Server_check_failed') as string,
      description: this.#translateService.instant('help_modal.TEXT.Your_network_is_blocking') as string,
      text: this.#translateService.instant('help_modal.TEXT.Ask_your_IT') as string,
      icon: 'icon icon-server',
      values: this.#errorDomains.join(';\n '),
      dataIdeals: 'copy-urls-issues',
    };
  }

  override getErrorReports(): { [key: string]: ErrorReport<ErrorReportDetails>[]; } {
    return { servers: this.#errorReports };
  }

  override getStatus(): DiagnosticStatus {
    return this.#status;
  }

  override run(): Observable<void> {
    this.#status = 'InProgress';

    const checkUrls$ = this.#config.healthCheckUrls.map((url) => {
      return this.#httpClient.get(url, { responseType: 'text' })
        .pipe(
          map(() => null),
          catchError((error: HttpErrorResponse) => {
            return of(this.#createErrorReport(error));
          }),
        );
    });

    return forkJoin(checkUrls$)
      .pipe(
        tap((reports: (ErrorReport<ErrorReportDetails> | null)[]) => {
          this.#errorReports = reports.filter((r) => !!r);
          this.#errorDomains = this.#errorReports.map((r) => r.details.domain);
          this.#status = this.#errorReports.length ? 'Error' : 'Completed';
        }),
        switchMapToVoid,
      );
  }

  #createErrorReport(error: HttpErrorResponse): ErrorReport<ErrorReportDetails> {
    const errorBaseReport = this.createErrorBaseReport(error);
    const domain = error?.url ? new URL(error.url).hostname : '';

    return {
      ...errorBaseReport,
      details: { domain },
    };
  }
}
